import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import ButtonBase from '@material-ui/core/ButtonBase';
import Typography from '@material-ui/core/Typography';
import Mixpanel from 'utils/mixpanelService';
import RichTextReactRenderer from 'components/RichTextReactRenderer';
import PageContext from '../PageContext';

const useStyles = makeStyles(theme => ({
  title: {
    color: props => props.introColor || '#fff',
    lineHeight: props => props.lineHeight || '50px',
    fontSize: props => props.fontSize || 37,
    maxWidth: props => props.shouldBreakTitle && 'max(700px, 650px + 8.5vw)',
    fontFamily: theme.typography.homeHeroHeaderFont,
    fontWeight: 700,
    display: '-webkit-box',
    margin: '0px 0px 6px',
    '-webkit-line-clamp': '3',
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    wordBreak: 'keep-all',
    [theme.breakpoints.down('1140')]: {
      fontSize: 28,
    },
  },
  subTitle: {
    color: props => props.introColor || '#fff',
    lineHeight: '22px',
    marginBottom: 10,
    fontSize: 17,
    display: '-webkit-box',
    margin: '0 auto',
    '-webkit-line-clamp': '2',
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    [theme.breakpoints.down('1140')]: {
      fontSize: 14,
    },
    [theme.breakpoints.down('480')]: {
      marginBottom: props => props.variant === 'E' && 16,
    },
  },
  introFooter: {
    paddingTop: '0 !important',
    paddingBottom: '0 !important',
    marginBottom: 17,
    paddingRight: props => props.paddingRight || 0,
    maxWidth: props => `${props.maxWidth}px` || 'none',
    flex: '1 auto',
    '& *': {
      color: props => props.introColor || '#fff',
    },
    '& p': {
      ...theme.typography.body2,
      margin: 0,
      color: props => props.introColor || '#fff',
    },
    '& ul': {
      listStyleType: 'none',
      padding: 0,
      margin: 0,
    },
    '& a': {
      color: props => props.introColor || '#fff',
    },
  },
  button: {
    ...theme.typography.body2,
    backgroundColor: props =>
      props.buttonBackgroundColor || theme.palette.primary.main,
    color: props => props.buttonFontColor || '#FFF',
    padding: '16px 24px',
    borderRadius: 28,
    height: 55,
    marginBottom: 12,
  },
  cta: {
    paddingTop: '0 !important',
    paddingBottom: '0 !important',
    marginBottom: 17,
    paddingRight: props => props.paddingRight || 0,
    maxWidth: props => `${props.maxWidth}px` || 'none',
    flex: '1 auto',
    '& *': {
      color: props => props.ctaColor || '#fff',
    },
    '& p': {
      ...theme.typography.body2,
      margin: 0,
      minHeight: 0,
      fontSize: props => props.ctaFontSize || theme.typography.body2.fontSize,
    },
    '& ul': {
      listStyleType: 'none',
      padding: 0,
      margin: 0,
    },
  },
  grid: {
    flex: '1 auto', // ie11
  },
}));

function Intro({
  fontSize,
  isLandingPage = true,
  shouldBreakTitle = false,
  otherLanguage,
  twoCardStyle,
  maxWidth,
  variant,
}) {
  const { clientInfo, textColor } = useContext(PageContext);
  const history = useHistory();
  const {
    pageText,
    fontColor,
    intro,
    heroCta,
    heroFooter,
    slug,
  } = otherLanguage ? clientInfo[1] || clientInfo[0] : clientInfo[0];
  const introTitle = _get(pageText, 'hero.header');
  const subTitle = _get(pageText, 'hero.body');
  const introColor = _get(pageText, 'hero.introColor') || fontColor;
  const button = _get(pageText, 'hero.button');

  const classes = useStyles({
    introColor: introColor ? `#${introColor}` : textColor,
    fontSize: fontSize || (twoCardStyle ? 30 : 37),
    lineHeight: twoCardStyle ? '45px' : '50px',
    paddingRight: twoCardStyle ? 56 : 0,
    ...(maxWidth ? { maxWidth } : {}),
    buttonBackgroundColor: _get(button, 'backgroundColor'),
    buttonFontColor: _get(button, 'color'),
    ctaFontSize: _get(pageText, 'hero.ctaFontSize'),
    ctaColor: _get(pageText, 'hero.ctaColor'),
    shouldBreakTitle,
    variant,
  });

  const handleButtonClick = () => {
    history.push(_get(button, 'url'));
    Mixpanel.track('Home Hero - Button - Clicked', {
      slide: slug,
    });
  };

  return (
    <>
      {introTitle && (
        <Grid item xs={12} className={classes.grid}>
          <Typography className={classes.title}>{introTitle}</Typography>
        </Grid>
      )}
      {intro && (
        <Grid item xs={12} className={classes.introFooter}>
          <RichTextReactRenderer
            data={intro.json}
            type="title"
            isLandingPage={isLandingPage}
            withHeroLinksStyles
          />
        </Grid>
      )}
      {subTitle && !intro && (
        <Grid item xs={12} className={classes.grid}>
          <Typography className={classes.subTitle}>{subTitle} </Typography>
        </Grid>
      )}
      {!_isEmpty(button) && (
        <ButtonBase
          disableRipple
          disableTouchRipple
          className={classes.button}
          onClick={handleButtonClick}
        >
          {button.label}
        </ButtonBase>
      )}
      {heroCta && (
        <Grid item xs={12} className={classes.cta}>
          <RichTextReactRenderer
            data={heroCta.json}
            type="title"
            isLandingPage={isLandingPage}
            withHeroLinksStyles
          />
        </Grid>
      )}
      {heroFooter && (
        <Grid item xs={12} className={classes.introFooter}>
          <RichTextReactRenderer
            data={heroFooter.json}
            type="title"
            isLandingPage={isLandingPage}
            withHeroLinksStyles
          />
        </Grid>
      )}
    </>
  );
}

Intro.propTypes = {
  fontSize: PropTypes.number,
  isLandingPage: PropTypes.bool,
  otherLanguage: PropTypes.bool,
  shouldBreakTitle: PropTypes.bool,
  maxWidth: PropTypes.number,
  variant: PropTypes.string,
};

export default React.memo(Intro);
